/**
 * splitChars, reveal-down / reveal-word / reveal-char / reveal-wrap + MutationObserver
 */

type ScrollTriggerApi = { refresh: () => void };

function getGsapScrollTrigger(): {
    gsap: NonNullable<typeof window.gsap>;
    ST: ScrollTriggerApi;
} | null {
    const gsap = window.gsap;
    const ST = (window as unknown as { ScrollTrigger?: ScrollTriggerApi })
        .ScrollTrigger;
    if (!gsap || !ST) return null;
    return { gsap, ST };
}

export class HomeTextEffects {
    static init(): void {
        HomeTextEffects.initSplitChars();
        HomeTextEffects.initRevealDown();
        HomeTextEffects.initRevealWord();
        HomeTextEffects.initRevealChar();
        HomeTextEffects.initRevealWrap();
    }

    static initSplitChars(): void {
        window.splitChars = function (
            target: Element | Element[] | NodeListOf<Element> | null | undefined,
        ): void {
            const els =
                target instanceof Element
                    ? [target]
                    : target
                      ? Array.from(target)
                      : [];

            els.forEach((el) => {
                if (!el) return;

                let source = el.getAttribute("data-origin-text");
                if (!source) {
                    source = (
                        el.getAttribute("aria-label") ||
                        el.textContent ||
                        ""
                    ).trim();
                    el.setAttribute("data-origin-text", source);
                }
                if (!source) return;

                el.setAttribute("aria-label", source);
                el.textContent = "";

                const frag = document.createDocumentFragment();
                const words = source.split(" ");

                words.forEach((word, i) => {
                    if (!word) return;
                    const wordWrap = document.createElement("span");
                    wordWrap.className = "word";
                    wordWrap.style.display = "inline-block";
                    wordWrap.style.whiteSpace = "nowrap";
                    for (let j = 0; j < word.length; j++) {
                        const s = document.createElement("span");
                        s.className = "char";
                        s.textContent = word[j];
                        wordWrap.appendChild(s);
                    }
                    frag.appendChild(wordWrap);
                    if (i < words.length - 1) {
                        const space = document.createElement("span");
                        space.className = "space";
                        space.textContent = "\u00A0";
                        frag.appendChild(space);
                    }
                });
                el.appendChild(frag);
                (el as HTMLElement).dataset.splitDone = "1";
            });
        };
    }

    static initRevealDown(): void {
        function wrap(el: HTMLElement): void {
            const elAny = el as HTMLElement & { __rdInit?: boolean };
            if (elAny.__rdInit) return;

            const text = (el.textContent || "").replace(/\s+/g, " ").trim();
            if (!text) return;

            elAny.__rdInit = true;
            el.setAttribute("aria-label", text);
            el.textContent = "";

            const frag = document.createDocumentFragment();
            for (let i = 0; i < text.length; i++) {
                const ch = text[i] === " " ? "\u00A0" : text[i];
                const rd = document.createElement("span");
                rd.className = "rd";
                const a = document.createElement("span");
                a.className = "char a";
                a.style.setProperty("--i", String(i));
                a.textContent = ch;
                const b = document.createElement("span");
                b.className = "char b";
                b.style.setProperty("--i", String(i));
                b.textContent = ch;
                rd.appendChild(a);
                rd.appendChild(b);
                frag.appendChild(rd);
            }
            el.appendChild(frag);

            if (el.dataset.rdDur)
                el.style.setProperty("--dur", el.dataset.rdDur);
            if (el.dataset.rdStagger)
                el.style.setProperty("--stagger", el.dataset.rdStagger);
            if (el.dataset.rdLift)
                el.style.setProperty("--lift", el.dataset.rdLift);
        }

        function init(sel = ".reveal-down"): void {
            document.querySelectorAll(sel).forEach((n) => wrap(n as HTMLElement));
        }

        if (document.readyState !== "loading") init();
        else document.addEventListener("DOMContentLoaded", () => init());

        const mo = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (!(node instanceof Element)) return;
                    if (node.matches?.(".reveal-down")) wrap(node as HTMLElement);
                    node.querySelectorAll?.(".reveal-down").forEach((el) =>
                        wrap(el as HTMLElement),
                    );
                });
            });
        });
        mo.observe(document.documentElement, { childList: true, subtree: true });
        (window as unknown as { initRevealDown: typeof init }).initRevealDown =
            init;
    }

    static initRevealWord(): void {
        const pair = getGsapScrollTrigger();
        if (!pair) return;
        const { gsap, ST } = pair;
        gsap.registerPlugin(ST);

        function splitByWord(el: HTMLElement): void {
            const elAny = el as HTMLElement & { __rwWordInit?: boolean };
            if (elAny.__rwWordInit) return;
            const text = (el.textContent || "").trim();
            if (!text) return;
            elAny.__rwWordInit = true;
            el.setAttribute("aria-label", text);
            el.textContent = "";
            const frag = document.createDocumentFragment();
            text.split(/(\s+)/).forEach((tok) => {
                if (/\s+/.test(tok)) {
                    frag.appendChild(document.createTextNode(tok));
                } else {
                    const wwrap = document.createElement("span");
                    wwrap.className = "word-wrap";
                    const word = document.createElement("span");
                    word.className = "word";
                    word.textContent = tok;
                    wwrap.appendChild(word);
                    frag.appendChild(wwrap);
                }
            });
            el.appendChild(frag);
        }

        function setup(el: HTMLElement): void {
            splitByWord(el);
            const words = el.querySelectorAll(".word");
            if (!words.length) return;
            gsap.to(words, {
                y: 0,
                opacity: 1,
                duration: 1.6,
                ease: "power3.out",
                stagger: 0.12,
                scrollTrigger: {
                    trigger: el,
                    start: "top 90%",
                    once: true,
                    invalidateOnRefresh: true,
                },
            });
        }

        function init(): void {
            document.querySelectorAll(".reveal-word").forEach((el) =>
                setup(el as HTMLElement),
            );
            ST.refresh();
        }
        if (document.readyState !== "loading") init();
        else document.addEventListener("DOMContentLoaded", init);
    }

    static initRevealChar(): void {
        const pair = getGsapScrollTrigger();
        if (!pair) return;
        const { gsap, ST } = pair;
        gsap.registerPlugin(ST);

        function splitByChar(el: HTMLElement): void {
            const elAny = el as HTMLElement & { __rcInit?: boolean };
            if (elAny.__rcInit) return;
            const text = (el.textContent || "").trim();
            if (!text) return;
            elAny.__rcInit = true;
            el.setAttribute("aria-label", text);
            el.textContent = "";
            const frag = document.createDocumentFragment();
            text.split(/(\s+)/).forEach((tok) => {
                if (/\s+/.test(tok)) {
                    frag.appendChild(document.createTextNode(tok));
                } else {
                    [...tok].forEach((ch) => {
                        const wwrap = document.createElement("span");
                        wwrap.className = "char-wrap";
                        const c = document.createElement("span");
                        c.className = "char";
                        c.textContent = ch;
                        wwrap.appendChild(c);
                        frag.appendChild(wwrap);
                    });
                }
            });
            el.appendChild(frag);
        }

        function setup(
            el: HTMLElement,
            opts: {
                start?: string;
                duration?: number;
                ease?: string;
                stagger?: number;
                once?: boolean;
            } = {},
        ): void {
            const {
                start = "top 85%",
                duration = 0.8,
                ease = "power3.out",
                stagger = 0.05,
                once = true,
            } = opts;
            splitByChar(el);
            const chars = el.querySelectorAll(".char");
            if (!chars.length) return;
            gsap.set(chars, { yPercent: 150, opacity: 0 });
            gsap.to(chars, {
                yPercent: 0,
                opacity: 1,
                duration,
                ease,
                stagger,
                scrollTrigger: { trigger: el, start, once },
            });
        }

        function init(sel = ".reveal-char"): void {
            document.querySelectorAll(sel).forEach((el) =>
                setup(el as HTMLElement),
            );
        }
        if (document.readyState !== "loading") init();
        else document.addEventListener("DOMContentLoaded", () => init());

        const mo = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (!(node instanceof Element)) return;
                    if (node.matches?.(".reveal-char"))
                        setup(node as HTMLElement);
                    node.querySelectorAll?.(".reveal-char").forEach((el) =>
                        setup(el as HTMLElement),
                    );
                });
            });
        });
        mo.observe(document.documentElement, { childList: true, subtree: true });
        (window as unknown as { initRevealChar: typeof init }).initRevealChar =
            init;
    }

    static initRevealWrap(): void {
        const pair = getGsapScrollTrigger();
        if (!pair) return;
        const { gsap, ST } = pair;
        gsap.registerPlugin(ST);

        function ensureInner(el: HTMLElement): void {
            const elAny = el as HTMLElement & { __rwInit?: boolean };
            if (!el || elAny.__rwInit) return;
            const text = el.textContent ?? "";
            const inner = document.createElement("span");
            inner.className = "inner";
            inner.textContent = text;
            el.textContent = "";
            el.appendChild(inner);
            elAny.__rwInit = true;
        }

        function setupRevealWrap(
            wrap: HTMLElement,
            opts: {
                start?: string;
                duration?: number;
                ease?: string;
                stagger?: number;
                once?: boolean;
            } = {},
        ): void {
            const {
                start = "top 85%",
                duration = 0.8,
                ease = "power2.out",
                stagger = 0.12,
                once = true,
            } = opts;
            const items = [...wrap.querySelectorAll(".reveal")];
            if (!items.length) return;
            items.forEach((item) => ensureInner(item as HTMLElement));
            const inners = items
                .map((item) => item.querySelector(".inner"))
                .filter(Boolean) as HTMLElement[];
            if (!inners.length) return;
            gsap.fromTo(
                inners,
                { y: "100%" },
                {
                    y: "0%",
                    duration,
                    ease,
                    stagger,
                    scrollTrigger: { trigger: wrap, start, once },
                },
            );
        }

        function init(sel = ".reveal-wrap"): void {
            document.querySelectorAll(sel).forEach((w) =>
                setupRevealWrap(w as HTMLElement),
            );
        }
        if (document.readyState !== "loading") init();
        else document.addEventListener("DOMContentLoaded", () => init());

        const mo = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (!(node instanceof Element)) return;
                    if (node.matches?.(".reveal-wrap"))
                        setupRevealWrap(node as HTMLElement);
                    node.querySelectorAll?.(".reveal-wrap").forEach((w) =>
                        setupRevealWrap(w as HTMLElement),
                    );
                });
            });
        });
        mo.observe(document.documentElement, { childList: true, subtree: true });
        (window as unknown as { initRevealWrap: typeof init }).initRevealWrap =
            init;
    }
}

declare global {
    interface Window {
        splitChars?: (
            target: Element | Element[] | NodeListOf<Element> | null | undefined,
        ) => void;
    }
}
